import { Loader2, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * One action the bar offers.
 *
 * `count` is how many rows the action would actually touch — NOT how many are
 * selected. A selection of 30 invoices that holds 12 drafts approves 12, and
 * the button has to say 12, because the button IS the last thing the user reads
 * before it happens.
 */
export interface BulkAction {
  id: string;
  /** Verb only; the bar appends the count. */
  label: string;
  icon?: ReactNode;
  count: number;
  variant?: 'default' | 'outline';
  onAct: () => void;
}

/**
 * The bar that appears when rows are selected.
 *
 * Sits in the page flow rather than floating over the table: an overlay covers
 * the last row of exactly the list the user is picking from, and a list page's
 * scrollport is the table, not the page, so there is nothing to scroll it out
 * from under.
 *
 * Actions fire on the click — there is no confirm step. That puts the whole
 * weight of being understood on the button itself, which is why the count is
 * rendered there and not only in the summary: "Approve 1,842" has to read as
 * the sentence it is. Everything the bar offers is reversible while the invoice
 * is unsent, which is what makes acting immediately reasonable.
 */
export function BulkActionBar({
  open,
  summary,
  detail,
  actions,
  onClear,
  busy,
  busyAction,
  emphasis,
}: {
  open: boolean;
  /** The headline count, e.g. "12 invoices selected". */
  summary: string;
  /** The second line: what the selection covers, and the link that widens or
   *  narrows it. */
  detail?: ReactNode;
  actions: BulkAction[];
  onClear: () => void;
  busy?: boolean;
  /** The `id` of the action actually in flight. Without it every button spins
   *  while one request runs, which reads as two operations against the same
   *  invoices — on a bar whose whole job is precision about what it is doing. */
  busyAction?: string;
  /** Set when the selection reaches beyond the page, so the bar looks
   *  different from an ordinary one — the visual tell that Approve now means
   *  more than what is on screen. */
  emphasis?: boolean;
}) {
  if (!open) return null;

  return (
    <section
      aria-label="Bulk actions"
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-lg border bg-popover px-4 py-2.5 shadow-lg',
        'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2',
        emphasis && 'border-primary/40',
      )}
    >
      <div className="flex min-w-0 flex-col">
        <span className="font-semibold text-sm tabular-nums">{summary}</span>
        {detail ? <span className="text-muted-foreground text-xs">{detail}</span> : null}
      </div>

      <div className="ml-auto flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onClear} disabled={busy}>
          <X className="size-3.5" />
          Clear
        </Button>
        {actions.map((action) => (
          <Button
            key={action.id}
            size="sm"
            variant={action.variant ?? 'default'}
            // Zero is not a reason to hide the button — a selection that holds
            // no drafts should show why Approve is unavailable, not silently
            // drop the control. It is also what keeps a click from posting an
            // empty target now that nothing intervenes.
            disabled={action.count === 0 || busy}
            onClick={action.onAct}
          >
            {busy && busyAction === action.id ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              action.icon
            )}
            {action.label} {action.count.toLocaleString()}
          </Button>
        ))}
      </div>
    </section>
  );
}
